import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { graphBuilderApi } from '../../../services/api';
import '../ProcessListPage.css';
import './ChatAssistantPage.css';

function ChatAssistantEditPage() {
  const { assistantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [copied, setCopied] = useState(false);

  const [record, setRecord] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('active');
  const [title, setTitle] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [suggestedText, setSuggestedText] = useState('');

  const publicUrl = `${window.location.origin}/chatassist/${assistantId}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await graphBuilderApi.getChatAssistant(assistantId);
      setRecord(resp);
      const config = resp.config || {};
      setName(resp.name || '');
      setDescription(resp.description || '');
      setStatus(resp.status || 'active');
      setTitle(config.title || '');
      setWelcomeMessage(config.welcome_message || '');
      setSuggestedText((config.suggested_questions || []).join('\n'));
    } catch (err) {
      console.error('Error loading chat assistant:', err);
      setError(err.message || 'Failed to load chat assistant');
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const baseConfig = (record && record.config) || {};
      const suggested_questions = suggestedText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const config = {
        ...baseConfig,
        title: title || null,
        welcome_message: welcomeMessage || null,
        suggested_questions,
      };
      await graphBuilderApi.updateChatAssistant(assistantId, {
        name,
        description,
        status,
        config,
      });
      setSavedAt(new Date());
      await load();
    } catch (err) {
      console.error('Error saving chat assistant:', err);
      setError(err.message || 'Failed to save chat assistant');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = publicUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="process-list-page">
        <div className="process-list-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <span>Loading chat assistant...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="process-list-page">
      <div className="process-list-container">
        <div className="process-list-header">
          <div className="header-left">
            <h1>Edit Chat Assistant</h1>
            <p className="subtitle">Update presentation and status. SNN settings are locked to the published snapshot.</p>
          </div>
          <div className="header-right">
            <button className="btn-secondary" onClick={() => navigate('/user-home/chat-assistant')}>
              Back to List
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {savedAt && !error && (
          <div className="ca-saved-banner">Saved at {savedAt.toLocaleTimeString()}</div>
        )}

        <div className="ca-edit-form">
          <div className="ca-field ca-url-field">
            <label>Public URL</label>
            <div className="ca-url-row">
              <input type="text" value={publicUrl} readOnly />
              <button type="button" className="btn-secondary" onClick={handleCopyUrl}>
                {copied ? 'Copied!' : 'Copy URL'}
              </button>
              <a className="btn-secondary" href={publicUrl} target="_blank" rel="noreferrer">Open</a>
            </div>
            <span className="ca-hint">Embed this URL in an iframe in your external application.</span>
          </div>

          <div className="ca-field">
            <label>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Assistant name" />
          </div>

          <div className="ca-field">
            <label>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Internal description" />
          </div>

          <div className="ca-field">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active (public URL works)</option>
              <option value="draft">Draft (public URL returns 404)</option>
              <option value="archived">Archived (public URL returns 404)</option>
            </select>
          </div>

          <hr className="ca-divider" />
          <h3 className="ca-section-title">Chat presentation</h3>

          <div className="ca-field">
            <label>Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Shown in the chat header" />
          </div>

          <div className="ca-field">
            <label>Welcome message</label>
            <textarea value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} rows={3} placeholder="First message shown to the user" />
          </div>

          <div className="ca-field">
            <label>Suggested questions <span className="ca-hint">(one per line)</span></label>
            <textarea value={suggestedText} onChange={(e) => setSuggestedText(e.target.value)} rows={4} placeholder={'How do I appeal a denial?\nWhat documents do I need?'} />
          </div>

          <div className="ca-field">
            <label>Target graph</label>
            <input type="text" value={record?.graph_id || '—'} readOnly />
            <span className="ca-hint">To change the SNN configuration, re-publish from the Test Graph page.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatAssistantEditPage;
